import React, { useState } from 'react';
import { Button, Menu, MenuItem, Chip, Stack, Box, Typography } from '@mui/material';
import { Person as PersonIcon, AssignmentInd as AssignIcon } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useCurrentPlan } from '../../billing/hooks/useCurrentPlan';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001/api/v1';
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/ai-crm`,
  headers: { 'Content-Type': 'application/json' },
});
apiClient.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('authToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

interface TeamMember {
  id: string;
  name: string | null;
  email: string;
  role: string;
}

interface Assignment {
  id: string;
  generatedLeadId: string;
  assignedToUserId: string;
}

const fetchTeam = async (): Promise<TeamMember[]> => {
  const res = await apiClient.get<{ success: boolean; data: TeamMember[] }>('/ai-assistant/team-members');
  return res.data.data ?? [];
};

const fetchAssignments = async (): Promise<Assignment[]> => {
  const res = await apiClient.get<{ success: boolean; data: Assignment[] }>('/lead-assignments');
  return res.data.data ?? [];
};

const assign = async ({ leadId, userId }: { leadId: string; userId: string | null }) => {
  await apiClient.put(`/lead-assignments/${leadId}`, { assignedToUserId: userId });
};

interface AssignLeadMenuProps {
  generatedLeadId: string;
}

const AssignLeadMenu: React.FC<AssignLeadMenuProps> = ({ generatedLeadId }) => {
  const { data: plan } = useCurrentPlan();
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const teamQ = useQuery({
    queryKey: ['team-members'],
    queryFn: fetchTeam,
    enabled: !!plan?.superAdminEnabled,
  });
  const assignmentsQ = useQuery({
    queryKey: ['lead-assignments'],
    queryFn: fetchAssignments,
    enabled: !!plan?.superAdminEnabled,
  });

  const mut = useMutation({
    mutationFn: assign,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lead-assignments'] }),
  });

  if (!plan?.superAdminEnabled) return null;

  const currentAssignment = assignmentsQ.data?.find((a) => a.generatedLeadId === generatedLeadId);
  const currentUser = teamQ.data?.find((u) => u.id === currentAssignment?.assignedToUserId);

  const handlePick = (userId: string | null) => {
    setAnchor(null);
    mut.mutate({ leadId: generatedLeadId, userId });
  };

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Button
        size="small"
        variant={currentAssignment ? 'contained' : 'outlined'}
        color={currentAssignment ? 'success' : 'primary'}
        startIcon={currentAssignment ? <AssignIcon sx={{ fontSize: 14 }} /> : <PersonIcon sx={{ fontSize: 14 }} />}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{ textTransform: 'none' }}
      >
        {currentUser
          ? (currentUser.name || currentUser.email.split('@')[0])
          : 'Assign'}
      </Button>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {(teamQ.data ?? []).map((u) => (
          <MenuItem key={u.id} selected={u.id === currentAssignment?.assignedToUserId} onClick={() => handlePick(u.id)}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon sx={{ fontSize: 16 }} />
              <Box>
                <Typography variant="body2" component="div">
                  {u.name || u.email}
                </Typography>
                {u.role === 'ADMIN' || u.role === 'SUPER_ADMIN' ? (
                  <Chip size="small" label="SuperAdmin" sx={{ height: 16, fontSize: 10 }} />
                ) : null}
              </Box>
            </Stack>
          </MenuItem>
        ))}
        {currentAssignment && [
          <MenuItem key="divider" disabled>
            <Typography variant="caption" color="text.secondary">— current —</Typography>
          </MenuItem>,
          <MenuItem key="unassign" onClick={() => handlePick(null)}>
            <Typography variant="body2" color="error">Unassign</Typography>
          </MenuItem>,
        ]}
      </Menu>
    </Box>
  );
};

export default AssignLeadMenu;
